import { Bell } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { featureFlags } from "shared/config/featureFlags";
import { useAuth } from "shared/contexts/Auth/AuthContext";
import { useNotificacoes } from "shared/hooks/useNotificacoes";
import { Notificacao } from "shared/components/notificacao/Notificacao";
import { buscarUsuario } from "shared/services/api/usuario.api";
import { Menu } from "../menu/Menu";
import styles from "./UserPanel.module.scss";

interface UserPanelProps {
  modo?: "completo" | "notificacoes";
}

export const UserPanel = ({
  modo = "completo",
}: UserPanelProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loadingAuth } = useAuth();
  const { unreadCount } = useNotificacoes();

  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [nomeUsuario, setNomeUsuario] = useState("");
  const [imagemUsuario, setImagemUsuario] = useState("");

  const notificationRef = useRef<HTMLDivElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  const isLogado = !!user;
  const somenteNotificacoes = modo === "notificacoes";

  /* ===============================
     USUÁRIO
  =============================== */

  useEffect(() => {
    if (somenteNotificacoes || !isLogado) {
      return;
    }

    async function carregarUsuario() {
      try {
        const usuario = await buscarUsuario();

        setNomeUsuario(
          usuario?.name ||
          usuario?.nome ||
          "Usuário"
        );

        setImagemUsuario(
          usuario?.avatarUrl ||
          usuario?.imageUser ||
          ""
        );
      } catch {
        setNomeUsuario("Usuário");
        setImagemUsuario("");
      }
    }

    carregarUsuario();
  }, [isLogado, somenteNotificacoes]);

  /* ===============================
     FECHAR MENUS AO CLICAR FORA
  =============================== */

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (
        notificationRef.current &&
        !notificationRef.current.contains(target)
      ) {
        setOpenNotifications(false);
      }

      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(target)
      ) {
        setOpenUserMenu(false);
      }
    }

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  /* ===============================
     FECHAR AO TROCAR DE ROTA
  =============================== */

  useEffect(() => {
    setOpenNotifications(false);
    setOpenUserMenu(false);
  }, [location.pathname]);

  /* ===============================
     LOADING
  =============================== */

  if (loadingAuth || !isLogado) {
    return null;
  }

  /* ===============================
     NOTIFICAÇÕES
  =============================== */

  const renderNotifications = () => {
    if (!featureFlags.notifications) {
      return null;
    }

    return (
      <div
        className={styles.notificationWrapper}
        ref={notificationRef}
      >
        <button
          type="button"
          className={styles.btnNotification}
          onClick={(event) => {
            event.stopPropagation();

            setOpenNotifications((prev) => !prev);
            setOpenUserMenu(false);
          }}
          aria-label="Minhas notificações"
          aria-expanded={openNotifications}
        >
          <Bell size={20} />

          {unreadCount > 0 && (
            <span className={styles.badge}>
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {openNotifications && (
          <div className={styles.popup}>
            <Notificacao
              limite={10}
              modo="popup"
            />

            <button
              type="button"
              className={styles.btnTodas}
              onClick={() => {
                setOpenNotifications(false);
                navigate("/todasnotificacoes");
              }}
            >
              Ver todas as notificações
            </button>
          </div>
        )}
      </div>
    );
  };

  /* ===============================
     SOMENTE NOTIFICAÇÕES
  =============================== */

  if (somenteNotificacoes) {
    return (
      <div className={styles.rightNavMobile}>
        {renderNotifications()}
      </div>
    );
  }

  /* ===============================
     RENDER COMPLETO
  =============================== */

  return (
    <div className={styles.rightNav}>
      <div className={styles.userArea}>
        {/* MOBILE */}

        <div
          className={`${styles.userBox} ${styles.userBoxMobile}`}
          onClick={() => navigate("/minhaconta")}
        >
          <img
            src={imagemUsuario || "/imagens/foto.png"}
            alt={nomeUsuario || "Profile"}
            className={styles.imgUser}
          />

          <div className={styles.textsContainer}>
            <span className={styles.bemVindo}>
              Bem-vindo(a)
            </span>

            <strong className={styles.nome}>
              {nomeUsuario || "Usuário"}
            </strong>
          </div>
        </div>

        {/* DESKTOP */}

        <div
          className={styles.userDesktop}
          ref={userMenuRef}
        >
          <button
            type="button"
            className={styles.userBoxDesktop}
            onClick={() => {
              setOpenUserMenu((prev) => !prev);
              setOpenNotifications(false);
            }}
          >
            <img
              src={imagemUsuario || "/imagens/foto.png"}
              alt={nomeUsuario || "Profile"}
              className={styles.imgUser}
            />

            <div className={styles.textsContainer}>
              <span className={styles.bemVindo}>
                Bem-vindo(a)
              </span>

              <strong className={styles.nome}>
                {nomeUsuario || "Usuário"}
              </strong>
            </div>
          </button>

          {openUserMenu && (
            <div className={styles.userMenu}>
              <Menu
                handleClose={() =>
                  setOpenUserMenu(false)
                }
              />
            </div>
          )}
        </div>
      </div>

      {renderNotifications()}
    </div>
  );
};
